"use client";

/**
 * Minimal cookie notice — we only use strictly-necessary cookies (session,
 * CSRF, theme preference). Under GDPR Art. 6(1)(b) and §25 TTDSG those
 * don't require opt-in consent, but visible transparency is the bar
 * corporate procurement (vanEck-style) expects to see on first visit.
 *
 * Behaviour:
 *  - Shown once per browser via ``localStorage`` flag
 *  - One "Got it" dismiss button + a link to the Privacy policy
 *  - Hidden during SSR to avoid hydration mismatch and FOUC; reveals
 *    after mount once we know the flag's state
 *
 * Not used: full consent flow (granular toggles, analytics opt-in,
 * marketing-cookie banner) — we have no analytics or marketing cookies
 * to gate, and adding the chrome would imply we do.
 */

import { useEffect, useState } from "react";
import Link from "next/link";

const STORAGE_KEY = "mi-cookie-notice-dismissed-v1";

export function CookieNotice() {
  // ``visible === null`` covers the SSR / first-paint window where we
  // can't yet read localStorage. Returning null in that branch means
  // the banner has no flicker on hydration.
  const [visible, setVisible] = useState<boolean | null>(null);

  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(STORAGE_KEY);
      setVisible(dismissed !== "1");
    } catch {
      // Some privacy-mode browsers throw on localStorage access. Skip
      // the banner entirely rather than re-prompting every page load.
      setVisible(false);
    }
  }, []);

  function dismiss() {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // Best-effort: if we can't persist, the banner reappears on the
      // next page load. Better than blocking the UI.
    }
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Cookie notice"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 text-sm">
        <p className="flex-1 min-w-0 text-text-secondary">
          We use strictly-necessary cookies to keep you signed in and to
          remember your interface preferences. No analytics, no marketing
          trackers.{" "}
          <Link
            href="/legal/privacy/v1"
            className="font-medium text-accent hover:text-accent-hover"
          >
            Privacy policy
          </Link>
          .
        </p>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 rounded-lg bg-accent px-4 py-1.5 text-xs font-medium text-accent-text hover:bg-accent-hover"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
